import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, catchError, first } from 'rxjs/operators';
import { NgxPermissionsService } from 'ngx-permissions';

import { environment } from '@env';
import { AuthMember } from '@shared/models';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    private memberSubject: BehaviorSubject<AuthMember>;
    public member: Observable<AuthMember>;

    private tokenSubject: BehaviorSubject<string>;
    public token: Observable<string>;
    
    private loggedInSubject: BehaviorSubject<boolean>;
    public loggedIn: Observable<boolean>;

    constructor(
        private router: Router,
        private http: HttpClient,
        private ngxPermissionsService: NgxPermissionsService
    ) {
        var member = null;
        var token = null;
        try {
            member = JSON.parse(localStorage.getItem('member'));
            token = localStorage.getItem('token');
        } catch (error) {
        
        }
        
        this.memberSubject = new BehaviorSubject<AuthMember>(member);
        this.member = this.memberSubject.asObservable();

        this.tokenSubject = new BehaviorSubject<string>(token);
        this.token = this.tokenSubject.asObservable();
        
        this.loggedInSubject = new BehaviorSubject<boolean>(this.checkIfLoggedIn());
        this.loggedIn = this.loggedInSubject.asObservable();
        
        this.token.subscribe(() => {
            this.loggedInSubject.next(this.checkIfLoggedIn());
        });
    }

    public get memberValue(): AuthMember {
        return this.memberSubject.value;
    }

    public get tokenValue(): String {
        return this.tokenSubject.value;
    }

    public get isLoggedIn(): boolean {
        return this.loggedInSubject.value;
    }

    checkIfLoggedIn() {
        return !!this.tokenSubject.value;
    }

    /* hasPermissionTo(name: string) {
        if (!this.checkIfLoggedIn()) {
            return false;
        }

        let permissions = this.memberSubject.value.permissions;
        if (!permissions) {
            return false;
        }

        return true;
    } */

    /**
     * Set current member
     * 
     * @param member 
     */
    private setMember(member: AuthMember) {
        if (member) {
            localStorage.setItem('member', JSON.stringify(member));
            this.memberSubject.next(member);

            // Permissions
            if (member.permissions) {
                this.ngxPermissionsService.loadPermissions(member.permissions);
            } else {
                this.ngxPermissionsService.flushPermissions();
            }
        } else {
            localStorage.removeItem('member');
            this.memberSubject.next(null);

            this.ngxPermissionsService.flushPermissions();
        }
    }

    /**
     * Set current token
     * 
     * @param token 
     */
    private setToken(token: string) {
        if (token) {
            localStorage.setItem('token', token);
            this.tokenSubject.next(token);
        } else {
            localStorage.removeItem('token');
            this.tokenSubject.next(null);
        }
        
        this.loggedInSubject.next(this.checkIfLoggedIn());
    }

    /**
     * Login
     * 
     * @param username 
     * @param password 
     */
    login(username: string, password: string): Observable<loginResponse> {
        return this.http.post<loginResponse>(
            `${environment.apiUrl}auth/login`,
            {
                username,
                password
            },
            {
                withCredentials: true,
                observe: 'response'
            }
        ).pipe(
            map(response => {
                const member = response.body.member;
                const token = response.body.token;
                
                this.setMember(member);
                this.setToken(token);

                return response.body;
            })
        );
    }

    /**
     * Get member data
     */
    authCheck(appVersion): Observable<getMemberResponse> {
        return this.http.post<getMemberResponse>(`${environment.apiUrl}auth/auth_check`, {
            app_version: appVersion,
        });
    }

    /**
     * Get member data
     */
    getMember(): Observable<getMemberResponse> {
        return this.http.get<getMemberResponse>(`${environment.apiUrl}auth/member`)
            .pipe(map(data => {
                if (data && data.member) {
                    // const newUser = data.user;
                    // var user = {...this.userSubject.value, ...newUser};

                    this.setMember(data.member);

                    return data;
                }
            }));
    }

    /**
     * Logout
     */
    async logout() {
        
        try {
            await this.http.get<any>(`${environment.apiUrl}auth/revoke_token`)
                .pipe(
                    // retry(2),
                    catchError(err => {
                        if (err.status === 401) {
                            this.logoutFromStorage(true);
                        }

                        return err;
                    }),
                    map(data => {
                        // remove member from local storage to log member out
                        this.logoutFromStorage(true);
                    }),
                    first()
                )
                .toPromise();
        } catch (e) {
            this.logoutFromStorage(true);
        }

        try {
            await this.http.get<any>(`${environment.apiUrl}auth/logout`, {
                withCredentials: true,
                observe: 'response'
            })
                .pipe(first())
                .toPromise();
        } catch (e) {
            
        }
    
        return true;
    }

    logoutFromStorage(redirect: boolean = false) {
        this.setMember(null);
        this.setToken(null);
        
        if(redirect) {
            this.router.navigate(['/login']);
        }
    }

    sendRequestUsernameEmail(email: string): Observable<sendRequestUsernameEmailResponse> {
        return this.http.post<sendRequestUsernameEmailResponse>(`${environment.apiUrl}auth/request_username`, { email })
    }

    sendRequestWaitinglistNumberEmail(old_household_number: string): Observable<sendRequestWaitinglistNumberEmailResponse> {
        return this.http.post<sendRequestWaitinglistNumberEmailResponse>(`${environment.apiUrl}auth/request_waitinglist_number`, { old_household_number })
    }

    sendPasswordResetEmail(username: string): Observable<sendPasswordResetEmailResponse> {
        return this.http.post<sendPasswordResetEmailResponse>(`${environment.apiUrl}auth/password/email`, { username })
    }
    
    checkPasswordResetToken(token: string): Observable<checkPasswordResetTokenResponse> {
        return this.http.post<checkPasswordResetTokenResponse>(`${environment.apiUrl}auth/password/check_token`, { token });
    }

    resetPassword(token: string, password: string, password_confirmation: string): Observable<resetPasswordResponse> {
        return this.http.post<resetPasswordResponse>(`${environment.apiUrl}auth/password/reset`, {
            token,
            password,
            password_confirmation
        })
    }
}

export interface loginResponse {
    member?: AuthMember;
    token?: string;
    message?: string;
}

export interface getMemberResponse {
    member: AuthMember
}

export interface sendRequestUsernameEmailResponse {
    success?: boolean;
    message?: string;
}

export interface sendRequestWaitinglistNumberEmailResponse {
    success?: boolean;
    message?: string;
}

export interface sendPasswordResetEmailResponse {
    success?: boolean;
    message?: string;
}

export interface checkPasswordResetTokenResponse {
    valid: boolean;
    member_id?: string;
    barcode?: string;
}

export interface resetPasswordResponse {
    success?: boolean;
    message?: string;
}